import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Clock, X } from 'lucide-react';
import Navbar from '../components/Navbar';
import { ContactSection, Footer } from '../components/Footer';
import { blogsApi, absoluteUploadUrl } from '../api/blogs';

export default function BlogListPage() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTag = searchParams.get('tag');

  useEffect(() => {
    document.title = activeTag
      ? `Blog posts tagged “${activeTag}” | EasyWorks`
      : 'Blog | EasyWorks — VPOB, GST & E-Commerce Insights';
    window.scrollTo(0, 0);
    (async () => {
      try { setBlogs(await blogsApi.listPublic()); } finally { setLoading(false); }
    })();
  }, [activeTag]);

  const filtered = activeTag ? blogs.filter((b) => (b.tags || []).includes(activeTag)) : blogs;

  return (
    <div className="min-h-screen bg-[#FAF2E8] font-[Inter]" data-testid="blog-list-page">
      <Navbar />
      <section className="bg-[#FAF2E8] pt-14 pb-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="text-xs uppercase tracking-wider text-[#F39200] font-semibold mb-3">Insights</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-neutral-900 leading-[1.05] max-w-3xl">
            The EasyWorks{' '}
            <span className="relative inline-block text-[#F39200]">blog
              <svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 90 10" fill="none">
                <path d="M2 7 C 30 1, 60 1, 88 6" stroke="#F39200" strokeWidth="2" strokeLinecap="round" fill="none" />
              </svg>
            </span>
          </h1>
          <p className="mt-5 text-[15px] md:text-base text-neutral-600 max-w-2xl">Practical guides on GST, VPOB, e-commerce accounting and compliance — from India&apos;s marketplace experts.</p>
          {activeTag && (
            <div className="mt-6 inline-flex items-center gap-2 bg-white border border-[#F39200]/40 rounded-full px-4 py-2 text-sm" data-testid="active-tag-filter">
              Filtered by <span className="font-semibold text-[#F39200]">#{activeTag}</span>
              <button onClick={() => setSearchParams({})} className="ml-1 text-neutral-500 hover:text-red-600"><X size={14}/></button>
            </div>
          )}
        </div>
      </section>

      <section className="bg-white py-14">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          {loading ? (
            <div className="text-center py-20 text-neutral-500 text-sm">Loading posts…</div>
          ) : filtered.length === 0 ? (
            <div className="bg-[#FAF2E8]/60 border border-dashed border-black/10 rounded-2xl p-14 text-center">
              <div className="text-xl font-semibold text-neutral-900">
                {activeTag ? `No posts tagged "${activeTag}" yet` : 'More posts coming soon'}
              </div>
              <p className="mt-2 text-sm text-neutral-600 max-w-md mx-auto">We&apos;re publishing weekly guides on VPOB, APOB, GST and marketplace accounting.</p>
              {activeTag && <button onClick={() => setSearchParams({})} className="mt-4 inline-flex items-center gap-1.5 bg-[#F39200] text-white text-sm font-semibold rounded-lg px-4 h-9">View all posts</button>}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((b) => (
                <Link key={b.id} to={`/blog/${b.slug}`} data-testid={`blog-card-${b.slug}`} className="group bg-white border border-black/5 rounded-2xl overflow-hidden hover:border-[#F39200] hover:shadow-lg transition-all flex flex-col">
                  {b.cover_image_url ? (
                    <div className="aspect-[16/9] overflow-hidden bg-neutral-100">
                      <img src={absoluteUploadUrl(b.cover_image_url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  ) : (
                    <div className="aspect-[16/9] bg-gradient-to-br from-[#F39200]/20 via-[#F39200]/5 to-transparent" />
                  )}
                  <div className="p-6 flex-1 flex flex-col">
                    {b.tags?.[0] && <span className="text-[10px] uppercase tracking-wider text-[#F39200] font-semibold">{b.tags[0]}</span>}
                    <h2 className="mt-2 text-lg font-bold text-neutral-900 group-hover:text-[#F39200] transition-colors leading-snug">{b.title}</h2>
                    {b.excerpt && <p className="mt-2 text-sm text-neutral-600 leading-relaxed line-clamp-3">{b.excerpt}</p>}
                    <div className="mt-5 flex items-center justify-between pt-4 border-t border-black/5">
                      <div className="text-[11px] text-neutral-500 flex items-center gap-1">
                        <Clock size={11} /> {new Date(b.published_at || b.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#F39200] group-hover:gap-2 transition-all">Read <ArrowRight size={12} /></span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
      <ContactSection />
      <Footer />
    </div>
  );
}
